import React from 'react';
import { Link } from 'react-router-dom';
import ContentPage from '../components/ContentPage';

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": "How to Play Euchre",
  "description": "Complete rules guide for Euchre, the classic American trick-taking card game for 4 players in teams of 2.",
  "step": [
    { "@type": "HowToStep", "name": "Form Teams", "text": "Four players divide into two teams of two. Partners sit across from each other." },
    { "@type": "HowToStep", "name": "Deal Cards", "text": "Deal 5 cards to each player from a 24-card deck. Turn up the top remaining card to propose trump." },
    { "@type": "HowToStep", "name": "Call Trump", "text": "Players decide to order up the turned card as trump or pass. If all pass, a second round allows naming any other suit." },
    { "@type": "HowToStep", "name": "Play Tricks", "text": "Play 5 tricks. Follow suit if possible. The right bower (jack of trump) is the highest card, followed by the left bower." },
    { "@type": "HowToStep", "name": "Score Points", "text": "The making team scores 1 point for 3-4 tricks, 2 for a sweep. Defenders score 2 if they euchre the makers. First to 10 wins." }
  ]
};

export default function EuchreRulesPage() {
  return (
    <ContentPage
      title="Euchre Rules - How to Play the Classic Card Game"
      description="Learn the rules of Euchre, the classic American trick-taking card game. Complete guide to dealing, trump, bowers, scoring, and team play."
      path="/euchre-rules"
      jsonLd={jsonLd}
    >
      {/* Introduction */}
      <h2 style={{ color: '#fff', fontSize: 26, marginTop: 40, marginBottom: 16 }}>What Is Euchre?</h2>
      <p>
        Euchre is a classic American trick-taking card game that has been a staple of social gatherings, family
        reunions, and friendly competition for well over a century. Rooted in the traditions of the Midwest and
        Great Lakes region, Euchre holds a special place in the hearts of players from Michigan, Indiana, Ohio,
        Wisconsin, and beyond. Walk into almost any bar, lodge hall, or kitchen table in those states on a
        weeknight and there is a good chance you will find a Euchre game in progress.
      </p>
      <p>
        The game traces its origins to the early 19th century, likely evolving from the French game Ecarte and
        the Alsatian game Juckerspiel. German and French immigrants brought these card-playing traditions to
        the American heartland, where the game took on a life of its own. By the mid-1800s, Euchre was one of
        the most popular card games in the United States. The Joker card was actually invented specifically for
        Euchre in the 1850s, though modern Euchre typically does not use it.
      </p>
      <p>
        What makes Euchre enduringly popular is its blend of simplicity and depth. The rules are straightforward
        enough that a new player can learn them in a single hand, yet the strategic possibilities around trump
        calling, leading, and partnership play keep experienced players engaged for decades. Games move quickly,
        usually lasting about 20 to 30 minutes, making it perfect for tournament play or a casual few rounds
        after dinner.
      </p>

      {/* Players & Teams */}
      <h2 style={{ color: '#fff', fontSize: 26, marginTop: 40, marginBottom: 16 }}>Players and Teams</h2>
      <p>
        Standard Euchre is played by exactly four players divided into two teams of two. Partners sit across
        from each other at the table, so that play alternates between the two teams as it moves clockwise around
        the table. This seating arrangement is important because it means you and your partner never play
        consecutive cards in a trick, which shapes every strategic decision you make.
      </p>
      <p>
        Communication between partners is limited to the cards they play. Unlike some card games, table talk
        about your hand is not allowed in Euchre. You must read your partner's intentions through the cards they
        lead, the cards they throw off, and the trump calls they make or decline. This silent partnership is one
        of the game's great pleasures and challenges.
      </p>

      {/* The Deck */}
      <h2 style={{ color: '#fff', fontSize: 26, marginTop: 40, marginBottom: 16 }}>The Deck</h2>
      <p>
        Euchre uses a stripped deck rather than a full 52-card deck. The standard Euchre deck contains 24 cards:
        the 9, 10, Jack, Queen, King, and Ace of each of the four suits (hearts, diamonds, clubs, and spades).
        All cards ranked 2 through 8 are removed before play begins.
      </p>
      <p>
        Some regional variations use a 32-card deck that also includes the 7s and 8s. This larger deck is more
        common in certain parts of Canada and in British Euchre. The 32-card version adds a bit more
        unpredictability since there are more low cards in play, but the core rules remain the same. For the
        purposes of this guide, we will focus on the standard 24-card version, which is by far the most common
        in American play.
      </p>

      {/* Dealing */}
      <h2 style={{ color: '#fff', fontSize: 26, marginTop: 40, marginBottom: 16 }}>Dealing</h2>
      <p>
        The deal rotates clockwise after each hand. The dealer shuffles the 24-card deck and deals five cards
        to each player. Cards are dealt in a specific pattern: each player receives either two or three cards
        in the first pass, and then three or two in the second pass, so that everyone ends up with exactly five.
        The traditional dealing pattern is 3-2-3-2 on the first round and 2-3-2-3 on the second, though the
        exact alternation can vary by house rules as long as each player receives five cards total.
      </p>
      <p>
        After dealing, four cards remain. The dealer places these four cards face down in the center of the
        table. This small pile is called the kitty. The top card of the kitty is then turned face up for all
        players to see. This turned-up card is the proposed trump suit for the first round of bidding, and it
        plays a crucial role in shaping the hand.
      </p>

      {/* Trump Calling */}
      <h2 style={{ color: '#fff', fontSize: 26, marginTop: 40, marginBottom: 16 }}>Calling Trump</h2>
      <p>
        The trump-calling phase is where Euchre's strategic depth really begins. It takes place in up to two
        rounds of bidding.
      </p>

      <h3 style={{ color: '#fff', fontSize: 20, marginTop: 24, marginBottom: 12 }}>Round 1: The Turned-Up Card</h3>
      <p>
        Starting with the player to the dealer's left and moving clockwise, each player has the option to accept
        the suit of the turned-up card as trump or to pass. If any player says "pick it up" (or "order it up"),
        the suit of that card becomes trump. The dealer then picks up the turned-up card and adds it to their
        hand, discarding one card face down to the kitty. The player who ordered it up (or the dealer, if they
        accepted it themselves) becomes the "maker" and their team is responsible for taking at least three
        tricks.
      </p>
      <p>
        If all four players pass in the first round, the turned-up card is turned face down and a second round
        begins.
      </p>

      <h3 style={{ color: '#fff', fontSize: 20, marginTop: 24, marginBottom: 12 }}>Round 2: Naming Trump</h3>
      <p>
        In the second round, again starting to the dealer's left, each player may name any suit as trump except
        the suit of the turned-up card that was just rejected. If a player names a suit, that suit becomes trump
        and that player's team becomes the makers. If all four players pass again, the hand is considered a
        misdeal in most variations, and the deal passes to the next player. Some house rules use "stick the
        dealer," which forces the dealer to name a suit if everyone else passes in round two, preventing misdeals.
      </p>

      {/* Going Alone */}
      <h2 style={{ color: '#fff', fontSize: 26, marginTop: 40, marginBottom: 16 }}>Going Alone</h2>
      <p>
        When a player orders up or names trump, they have the option to declare that they are "going alone."
        This means the maker's partner sits out for that hand and does not play any cards. The lone player
        attempts to take all five tricks by themselves, playing against both opponents.
      </p>
      <p>
        Going alone is a calculated gamble. If the lone player takes all five tricks, their team scores four
        points instead of the usual one or two. This can be a game-changing swing, especially when a team is
        close to the winning threshold. However, if the lone player fails to take at least three tricks, the
        opposing team scores two points for the euchre, just as they would in a normal hand.
      </p>
      <p>
        You should consider going alone when you hold a dominant hand, typically including both bowers and at
        least one additional trump or a strong off-suit Ace. The risk is real, since your partner cannot help
        you win tricks, but the reward of four points makes it worthwhile with the right cards.
      </p>

      {/* Card Ranking */}
      <h2 style={{ color: '#fff', fontSize: 26, marginTop: 40, marginBottom: 16 }}>Card Ranking and the Bower System</h2>
      <p>
        Understanding card ranking in Euchre is essential because it differs from most other card games. The
        trump suit has a unique hierarchy that includes the famous "bower" system.
      </p>

      <h3 style={{ color: '#fff', fontSize: 20, marginTop: 24, marginBottom: 12 }}>Trump Suit Ranking (Highest to Lowest)</h3>
      <ul style={{ marginBottom: 16, paddingLeft: 28 }}>
        <li><strong>Right Bower</strong> - The Jack of the trump suit. This is the single highest card in the game.</li>
        <li><strong>Left Bower</strong> - The Jack of the same-color suit as trump. For example, if hearts are trump, the Jack of diamonds is the left bower. The left bower is considered a trump card for the duration of the hand, not a card of its printed suit.</li>
        <li><strong>Ace of Trump</strong></li>
        <li><strong>King of Trump</strong></li>
        <li><strong>Queen of Trump</strong></li>
        <li><strong>10 of Trump</strong></li>
        <li><strong>9 of Trump</strong></li>
      </ul>

      <h3 style={{ color: '#fff', fontSize: 20, marginTop: 24, marginBottom: 12 }}>Off-Suit Ranking (Highest to Lowest)</h3>
      <ul style={{ marginBottom: 16, paddingLeft: 28 }}>
        <li>Ace</li>
        <li>King</li>
        <li>Queen</li>
        <li>Jack (unless it is the left bower)</li>
        <li>10</li>
        <li>9</li>
      </ul>
      <p>
        The left bower is one of the most important concepts in Euchre. Because the Jack of the same-color suit
        switches allegiance to the trump suit, the off-suit of the same color is effectively missing its Jack
        for that hand. For instance, if spades are trump, the Jack of clubs becomes the left bower and is treated
        as a spade, not a club. This means clubs has only five cards in that hand: Ace, King, Queen, 10, and 9.
      </p>

      {/* Playing Tricks */}
      <h2 style={{ color: '#fff', fontSize: 26, marginTop: 40, marginBottom: 16 }}>Playing Tricks</h2>
      <p>
        After trump has been established, the actual play of the hand begins. Five tricks will be played, one
        card from each active player per trick.
      </p>
      <p>
        The player to the dealer's left leads the first trick by playing any card from their hand face up on the
        table. Play continues clockwise. Each subsequent player must follow suit if they can. Following suit
        means playing a card of the same suit that was led. Remember that the left bower counts as a trump card,
        not as its printed suit, so if clubs are led and you hold the Jack of clubs but clubs is not trump, you
        must play it to follow suit. However, if that Jack is the left bower (because spades are trump), it is
        now a spade and you are not required to play it when clubs are led.
      </p>
      <p>
        If a player cannot follow suit, they may play any card. They can choose to trump the trick by playing a
        trump card, or they can throw off (discard) a card from another suit. The trick is won by the highest
        trump card played, or if no trump was played, by the highest card of the suit that was led. The winner
        of each trick leads the next one.
      </p>

      {/* Scoring */}
      <h2 style={{ color: '#fff', fontSize: 26, marginTop: 40, marginBottom: 16 }}>Scoring</h2>
      <p>
        Scoring in Euchre is straightforward but has meaningful consequences for strategy.
      </p>
      <ul style={{ marginBottom: 16, paddingLeft: 28 }}>
        <li><strong>Makers win 3 or 4 tricks:</strong> The making team scores 1 point.</li>
        <li><strong>Makers win all 5 tricks (a sweep or march):</strong> The making team scores 2 points.</li>
        <li><strong>Makers win fewer than 3 tricks (euchred):</strong> The defending team scores 2 points. Getting euchred is a significant setback because you not only fail to score but also give your opponents two points.</li>
        <li><strong>Lone hand wins all 5 tricks:</strong> The maker's team scores 4 points. This is the maximum score possible in a single hand.</li>
        <li><strong>Lone hand wins 3 or 4 tricks:</strong> The maker's team scores 1 point (same as a regular hand).</li>
        <li><strong>Lone hand wins fewer than 3 tricks:</strong> The defending team scores 2 points for the euchre.</li>
      </ul>
      <p>
        The asymmetry of the scoring system is the engine of Euchre strategy. The makers put themselves at risk
        by calling trump. If they succeed, they gain only one point, but if they fail, they hand two points to
        the other side. This means you need to be reasonably confident in your hand before ordering up or naming
        trump. On the other hand, passing too often gives the other team more opportunities to make trump on
        their terms.
      </p>

      {/* Winning */}
      <h2 style={{ color: '#fff', fontSize: 26, marginTop: 40, marginBottom: 16 }}>Winning the Game</h2>
      <p>
        The first team to reach 10 points wins the game. In tournament play, games are sometimes played to 11
        or even 15 points, but 10 is the standard in casual and most organized play.
      </p>
      <p>
        A common scorekeeping tradition uses two 5-cards (or 6-cards) overlapping to display the score. By
        revealing pips on the cards as points are earned, players can quickly see the current score at a glance.
        This charming scoring method is part of Euchre's cultural identity and is still widely used even though
        pencil and paper would work just as well.
      </p>
      <p>
        Because games are played to only 10 points and each hand can produce 1, 2, or even 4 points, a game of
        Euchre moves quickly. Most games finish in 15 to 25 minutes, which makes Euchre an ideal game for
        tournament formats where many rounds need to be played in a single evening.
      </p>

      {/* Tips for New Players */}
      <h2 style={{ color: '#fff', fontSize: 26, marginTop: 40, marginBottom: 16 }}>Tips for New Players</h2>
      <p>
        If you are just learning Euchre, here are some principles to get you started on the right foot.
      </p>
      <ul style={{ marginBottom: 16, paddingLeft: 28 }}>
        <li><strong>Count your trump:</strong> Before calling trump, count how many trump cards you hold. Three or more trump cards, especially with a bower, is usually enough to consider calling.</li>
        <li><strong>Lead trump early:</strong> If you are the maker and you have strong trump, consider leading trump to strip your opponents of their trump cards. Once the opponents are out of trump, your off-suit winners become much more reliable.</li>
        <li><strong>Watch the left bower:</strong> Always be aware of where the left bower might be. Forgetting that the same-color Jack is now a trump card is the most common mistake beginners make.</li>
        <li><strong>Support your partner:</strong> If your partner orders up trump, trust them. Play your highest trump when you can help them win tricks. If they lead an off-suit Ace, do not trump over it.</li>
        <li><strong>Know when to pass:</strong> It is better to pass a mediocre hand than to call trump and get euchred. A two-point swing against you is a serious setback.</li>
      </ul>

      {/* Euchre vs Buck Euchre */}
      <h2 style={{ color: '#fff', fontSize: 26, marginTop: 40, marginBottom: 16 }}>How Euchre Differs from Buck Euchre</h2>
      <p>
        Standard Euchre and Buck Euchre share the same core mechanics of trick-taking, trump calling, and the
        bower system, but they differ in several important ways that fundamentally change the strategy and feel
        of the game.
      </p>
      <ul style={{ marginBottom: 16, paddingLeft: 28 }}>
        <li><strong>Team vs. Individual:</strong> Standard Euchre is a partnership game with two teams of two. Buck Euchre is an every-player-for-themselves game, typically played with three or four players. There are no permanent teams or partners.</li>
        <li><strong>Scoring Direction:</strong> In standard Euchre, you score points upward toward a goal of 10. In Buck Euchre, each player starts with a set number of points and loses points for taking tricks. The goal is to avoid being the first player knocked out.</li>
        <li><strong>Risk Profile:</strong> Standard Euchre rewards making trump. Buck Euchre penalizes taking tricks you did not want, making the decision to call trump much more dangerous. Sitting out (not calling trump) is a viable and often smart strategy in Buck Euchre.</li>
        <li><strong>Going Alone:</strong> In standard Euchre, going alone is a chance for bonus points. In Buck Euchre, every hand is effectively a solo effort since there are no partners to help you.</li>
        <li><strong>Player Count:</strong> Standard Euchre is designed for exactly four players. Buck Euchre accommodates three or four players with different dynamics in each configuration.</li>
      </ul>
      <p>
        If you enjoy standard Euchre and want a faster-paced, higher-stakes variant that tests individual skill
        rather than teamwork, Buck Euchre is an excellent next step. You can read the full rules in
        our <Link to="/buck-euchre-rules" style={{ color: '#58a6ff' }}>Buck Euchre Rules</Link> guide, or jump
        straight into a game and learn as you play.
      </p>
    </ContentPage>
  );
}
